import React, { useCallback, useMemo, useRef } from 'react';
import { Body, BodyBottom, Container, Head, HeadWrapper, RowNoDataWrapper, Th } from './styles';
import Paginator from './../Paginator/Paginator';
import PerPage from './../PerPage/PerPage';
import { useAppSelector } from '#app/hooks/useAppStore';
import { useSortableTable } from './hooks/useSortableTable';
import TableRow from '#app/components/Table/TableRow';
import LoaderContainer from '#app/components/LoaderContainer/LoaderContainer';

const PaginationTable = ({
  id,
  fields,
  data,
  onPageChange = () => {},
  onPerPageChange = () => {},
  rowStyle,
  hover,
  onRowClick = () => {},
  isLoading = false,
}) => {
  const i18n = useAppSelector((state) => state.i18n);
  const {
    sorted,
    handleSort,
    // sortItems,
    getWidth,
  } = useSortableTable();
  const tableRef = useRef();

  const propIndexOf = (arr = [], search, prop) => {
    for (let i = 0, len = arr.length; i < len; i++) {
      if (arr[i][prop] === search) return i;
    }
    return -1;
  };

  const sort = useCallback(
    (items) => {
      sorted.forEach((col) => {
        if (typeof col.sort === 'function') {
          items = items.sort((a, b) => col.sort(a, b));
        }

        if (col.direction === 'descending') {
          items = items.reverse();
        }
      });

      return items;
    },
    [sorted]
  );

  const renderRows = useCallback(() => {
    const fieldsArray = Object.keys(fields);
    let sortedData = sort([...(data?.data || [])]);

    return sortedData.map((item, arrayKey) => {
      const rowStyles = typeof rowStyle === 'function' ? rowStyle(item) : rowStyle;

      return (
        <TableRow
          key={item.id}
          item={item}
          fieldsArray={fieldsArray}
          fields={fields}
          arrayKey={arrayKey}
          rowStyle={rowStyles}
          onRowClick={onRowClick}
          hover={hover}
          getWidth={getWidth}
          getStyle={(field, key) => (field.hidden ? { display: 'none' } : {})}
          onTdClick={(item, fields, key, arrayKey) => {
            // Add any additional logic for onTdClick if needed
          }}
        />
      );
    });
  }, [data?.data, fields, rowStyle, onRowClick, hover, getWidth, sort]);

  const renderEmptyRow = useCallback(
    () => (
      <RowNoDataWrapper>
        <div>
          <p>{i18n.common.noData}</p>
        </div>
      </RowNoDataWrapper>
    ),
    [i18n]
  );

  const fieldsArray = useMemo(() => Object.keys(fields), [fields]);

  if (isLoading) {
    return (
      <Container>
        <LoaderContainer loader={true} height={'553px'} />
      </Container>
    );
  }

  return (
    <Container>
      <Head>
        <HeadWrapper>
          {fieldsArray.map((item, key) => {
            const direction =
              propIndexOf(sorted, key, 'column') !== -1
                ? sorted[propIndexOf(sorted, key, 'column')].direction
                : null;

            return (
              <Th
                key={key}
                className={direction === null ? '' : `sorted ${direction}`}
                align={fields[item].align ? fields[item].align : ''}
                onClick={() => handleSort(key, fields[item].sort)}
                flexWidth={getWidth(fields[item])}
                style={fields[item].hidden ? { display: 'none' } : {}}
              >
                {fields[item].name}
              </Th>
            );
          })}
        </HeadWrapper>
      </Head>
      <Body ref={tableRef} id={id}>
        {data?.data?.length > 0 ? renderRows() : renderEmptyRow()}
      </Body>
      <BodyBottom>
        <PerPage onPerPageChange={onPerPageChange} data={data} />
        <Paginator onPageChange={onPageChange} data={data} />
      </BodyBottom>
    </Container>
  );
};

export default PaginationTable;
